import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { ILead } from '@clickersphere/core/entities/ILead';
import { ResponseError } from '@clickersphere/core/entities/ResponseError';
import { LeadLostReasonEnum } from '@clickersphere/core/enums/LeadLostReasonEnum';
import { LEAD_LOST_REASON_VALUES } from '@clickersphere/core/enums/LeadLostReasonEnum';
import { useUpdateLead } from '../../../../app/hooks/lead/useUpdateLead';
import { useUpdateLeadStatus } from '../../../../app/hooks/lead/useUpdateLeadStatus';
import { useError } from '../../../../app/hooks/useError';
import useToast from '../../../../app/hooks/useToast';
import { useStore } from '../../../../app/store';
import { leadLostReasonLabelKeys } from '../leadEnumLabels';

const MAX_NOTE_LENGTH = 500;

export function useDeactivateLeadModalController(lead: ILead) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { handleError } = useError();
  const [lostReason, setLostReason] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [isPending, setIsPending] = useState(false);

  const { updateLeadAsync } = useUpdateLead();
  const { updateLeadStatusAsync } = useUpdateLeadStatus();

  const { handleCloseModal } = useStore(
    useShallow((state) => ({
      handleCloseModal: state.modal.handleCloseModal,
    })),
  );

  const reasonOptions = useMemo(
    () =>
      LEAD_LOST_REASON_VALUES.map((reason) => ({
        value: reason,
        text: t(leadLostReasonLabelKeys[reason]),
      })),
    [t],
  );

  const noteError =
    note.length > MAX_NOTE_LENGTH
      ? t('lead.deactivate.errors.noteMaxLength', { max: MAX_NOTE_LENGTH })
      : undefined;

  async function handleConfirm() {
    if (noteError) return;
    setIsPending(true);
    try {
      if (lostReason || note.trim()) {
        await updateLeadAsync({
          id: lead.id,
          data: {
            lostReason: lostReason
              ? (lostReason as LeadLostReasonEnum)
              : undefined,
            lostReasonNote: note.trim() ? note.trim() : undefined,
          },
        });
      }
      await updateLeadStatusAsync({ id: lead.id, value: false });
      handleCloseModal();
    } catch (error) {
      if (error instanceof ResponseError) {
        handleError(error);
      } else {
        toast(t('lead.deactivate.errors.generic'), 'error');
      }
    } finally {
      setIsPending(false);
    }
  }

  return {
    t,
    reasonOptions,
    lostReason,
    setLostReason,
    note,
    setNote,
    noteError,
    maxNoteLength: MAX_NOTE_LENGTH,
    isPending,
    handleConfirm,
    handleClose: handleCloseModal,
  };
}
